import { mergeTableStyle } from "./mergeTableStyle"
import {
  deepFreeze,
  EXCEL_DEFAULTS,
  LOW_BASE_DEFAULTS,
  SIG_DEFAULTS,
} from "./presets"
import type { CellStyleSpec, DeepPartial, TableStyle } from "./tableStyle.types"
import {
  DEFAULT_TABLE_STYLE_CHOICE,
  FONT_OPTIONS,
  isCustomised,
  LinesMode,
  PresetId,
  StyleTweaks,
  TableStyleChoice,
} from "types/tableStyle.types"

/**
 * User-selectable table looks. A look (preset tweaks + extras) sets fonts,
 * colours, borders and bands; the surface layout sets wrapping and widths,
 * so one look fits both the Query Console and the Excel report:
 *
 *   BASE_TABLE_STYLE → SURFACE_LAYOUTS[surface] → tweaks (preset, then the
 *   user's) → the preset's extras
 *
 * Extras never touch a field a tweak writes, so their order does not matter.
 */

/** Where a table is shown: the Query Console or an Excel report (screen and export). */
export type TableSurface = "console" | "report"

/** Shared defaults every look starts from. */
export const BASE_TABLE_STYLE: TableStyle = deepFreeze<TableStyle>({
  version: 1,
  font: { family: "inherit", sizePt: 10.5, color: "inherit" },
  header: {},
  stub: {},
  statLabel: {},
  body: {},
  totals: {},
  numberFormats: {},
  numberFormatSource: "engine",
  sig: SIG_DEFAULTS,
  lowBase: LOW_BASE_DEFAULTS,
  selection: { bg: "#e6f7ff" },
  density: "comfortable",
  excel: EXCEL_DEFAULTS,
})

const CONSOLE_CELL: CellStyleSpec = { wrap: true, minWidth: 80, maxWidth: 260 }
const REPORT_CELL: CellStyleSpec = { wrap: false }
const REPORT_BODY_CELL: CellStyleSpec = { wrap: false, maxHeightEm: 1.5 }

/** Layout only (wrapping, widths, density): today's console and report layouts. */
export const SURFACE_LAYOUTS: Record<TableSurface, DeepPartial<TableStyle>> = {
  console: {
    header: CONSOLE_CELL,
    subHeader: CONSOLE_CELL,
    stub: CONSOLE_CELL,
    statLabel: CONSOLE_CELL,
    body: CONSOLE_CELL,
    density: "compact",
  },
  report: {
    header: REPORT_CELL,
    subHeader: REPORT_CELL,
    stub: REPORT_BODY_CELL,
    statLabel: REPORT_BODY_CELL,
    body: REPORT_BODY_CELL,
    density: "comfortable",
  },
}

/** A built-in look: its name, its picker swatch and what it sets. */
export interface LookPreset {
  label: string
  /** Banner colour shown next to the name in pickers. */
  swatch: string
  tweaks: StyleTweaks
  /** Look details the drawer does not expose; never a tweak-owned field. */
  extras?: DeepPartial<TableStyle>
}

const BOLD_SECTIONS: DeepPartial<TableStyle> = {
  header: { bold: true },
  subHeader: { bold: true },
  stub: { bold: true },
  totals: { bold: true },
}

/** Navy banded's zebra fill, and the drawer's colour when zebra is turned on. */
export const DEFAULT_ZEBRA = "#f2f5fa"
/** Navy banded's Total shade, and the drawer's colour when it is turned on. */
export const DEFAULT_TOTAL_SHADE = "#e8edf6"

/** The built-in looks by id (spec 3.3). */
export const LOOK_PRESETS: Record<PresetId, LookPreset> = {
  classic: {
    label: "Classic grid",
    swatch: "#d9d9d9",
    tweaks: {
      fontFamily: "arial",
      fontSizePt: 10,
      textColor: "#000000",
      bannerFill: "#d9d9d9",
      bannerText: "#000000",
      subBannerFill: "#d9d9d9",
      subBannerText: "#000000",
      rowLabelFill: "#ffffff",
      rowLabelText: "#000000",
      rowLabelAlign: "left",
      lines: "grid",
      lineColor: "#bfbfbf",
      zebra: null,
      totalShade: null,
    },
    extras: BOLD_SECTIONS,
  },
  navy: {
    label: "Navy banded",
    swatch: "#1f3864",
    tweaks: {
      fontFamily: "calibri",
      fontSizePt: 10,
      textColor: "#1f1f1f",
      bannerFill: "#1f3864",
      bannerText: "#ffffff",
      subBannerFill: "#d9e1f2",
      subBannerText: "#1f3864",
      rowLabelFill: null,
      rowLabelText: "#1f3864",
      rowLabelAlign: "left",
      lines: "rows",
      lineColor: "#d9d9d9",
      zebra: DEFAULT_ZEBRA,
      totalShade: DEFAULT_TOTAL_SHADE,
    },
    extras: BOLD_SECTIONS,
  },
  minimal: {
    label: "Minimal rules",
    swatch: "#ffffff",
    tweaks: {
      fontFamily: "calibri",
      fontSizePt: 10,
      textColor: "#262626",
      bannerFill: null,
      bannerText: "#262626",
      subBannerFill: null,
      subBannerText: "#262626",
      rowLabelFill: null,
      rowLabelText: "#262626",
      rowLabelAlign: "left",
      lines: "rules",
      lineColor: "#262626",
      zebra: null,
      totalShade: null,
    },
    extras: BOLD_SECTIONS,
  },
  light: {
    label: "Light",
    swatch: "#f8fafc",
    tweaks: {
      fontFamily: "app",
      fontSizePt: 10.5,
      bannerFill: "#f8fafc",
      subBannerFill: "#f8fafc",
      rowLabelFill: "#f8fafc",
      rowLabelAlign: "center",
      lines: "grid",
      lineColor: "#e2e8f0",
      zebra: null,
      totalShade: null,
    },
    extras: {
      header: { bold: true },
      stub: { bold: true },
      statLabel: { bg: "#ffffff" },
      body: { bg: "#ffffff" },
    },
  },
}

/** The CSS font stack of a `FONT_OPTIONS` id. */
export const fontCss = (id?: string): string | undefined =>
  FONT_OPTIONS.find((font) => font.id === id)?.css

/**
 * A tweak fill: `null` (no fill) and unset both leave the base's (none).
 * Not for the sub-banner: see `subBannerFill`.
 */
const fill = (value: string | null | undefined): string | undefined =>
  value ?? undefined

/**
 * The sub-banner's fill. A sub-header cell is `header` then `subHeader`
 * (`resolveCellStyle`), so an unset `subHeader.bg` would show the banner's
 * fill: `null` (no fill) is written as "transparent" instead, which is no
 * background on screen and no fill in Excel. Unset stays unset.
 */
const subBannerFill = (value: string | null | undefined): string | undefined =>
  value === null ? "transparent" : value

const cells = (border: string, borderBottom: string): CellStyleSpec => ({
  border,
  borderBottom,
})

/**
 * Border fields of every section for a lines mode (spec 3.4). Every field is
 * written, "none" where there is no line.
 */
const linesLayer = (mode: LinesMode, color: string): DeepPartial<TableStyle> => {
  const none = "none"
  const thin = `1px solid ${color}`
  const heavy = `2px solid ${color}`
  switch (mode) {
    case "grid":
      return {
        header: cells(thin, thin),
        subHeader: cells(thin, thin),
        stub: cells(thin, thin),
        statLabel: cells(thin, thin),
        body: cells(thin, thin),
        bandRule: none,
        rules: { top: none, headerBottom: heavy, bottom: none },
      }
    case "rows":
      return {
        header: cells(none, thin),
        subHeader: cells(none, thin),
        stub: cells(none, none),
        statLabel: cells(none, none),
        body: cells(none, none),
        bandRule: thin,
        rules: { top: none, headerBottom: none, bottom: none },
      }
    case "rules":
      return {
        header: cells(none, thin),
        subHeader: cells(none, none),
        stub: cells(none, none),
        statLabel: cells(none, none),
        body: cells(none, none),
        bandRule: none,
        rules: { top: heavy, headerBottom: heavy, bottom: heavy },
      }
    case "none":
      return {
        header: cells(none, none),
        subHeader: cells(none, none),
        stub: cells(none, none),
        statLabel: cells(none, none),
        body: cells(none, none),
        bandRule: none,
        rules: { top: none, headerBottom: none, bottom: none },
      }
  }
}

/**
 * Contract layers of a set of tweaks (spec 3.2). Unset tweaks write nothing,
 * except the sub-banner, which would otherwise take the banner's values (a
 * sub-header cell layers `subHeader` over `header`): its `null` fill is
 * written as "transparent" and its unset text as the table's text colour.
 * The other `null` fills stay unset (`totals` and `zebra` must, or they
 * would blank the section fills under them).
 */
export const tweaksToLayers = (t: StyleTweaks): DeepPartial<TableStyle>[] => [
  {
    font: { family: fontCss(t.fontFamily), sizePt: t.fontSizePt, color: t.textColor },
    header: { bg: fill(t.bannerFill), color: t.bannerText },
    subHeader: {
      bg: subBannerFill(t.subBannerFill),
      color: t.subBannerText ?? t.textColor ?? "inherit",
    },
    stub: { bg: fill(t.rowLabelFill), color: t.rowLabelText, align: t.rowLabelAlign },
    statLabel: { align: t.rowLabelAlign },
    body: { zebra: fill(t.zebra) },
    totals: { bg: fill(t.totalShade) },
    sig: {
      upper: { fill: t.sigFill1 },
      lower: { fill: t.sigFill2 },
      arrowUp: { color: t.arrowUpColor },
      arrowDown: { color: t.arrowDownColor },
    },
    numberDecimals: { percent: t.percentDecimals, mean: t.meanDecimals },
    excel: {
      stubWidth: t.excelStubWidth,
      dataWidth: t.excelDataWidth,
      freezePanes: t.freezePanes,
    },
  },
  t.lines ? linesLayer(t.lines, t.lineColor ?? "#bfbfbf") : {},
]

/** The preset's tweaks with the user's on top. */
export const effectiveTweaks = (choice: TableStyleChoice): StyleTweaks => {
  const preset = LOOK_PRESETS[choice.preset] ?? LOOK_PRESETS.classic
  const user: StyleTweaks & { v?: number } = { ...choice.tweaks }
  delete user.v
  return { ...preset.tweaks, ...user }
}

/** The complete style of a choice on a surface (spec 3.6). */
export const resolveTableStyle = (
  surface: TableSurface,
  choice: TableStyleChoice = DEFAULT_TABLE_STYLE_CHOICE
): TableStyle => {
  const preset = LOOK_PRESETS[choice.preset] ?? LOOK_PRESETS.classic
  return mergeTableStyle(
    BASE_TABLE_STYLE,
    SURFACE_LAYOUTS[surface],
    ...tweaksToLayers(effectiveTweaks(choice)),
    preset.extras
  )
}

/** "Navy banded", or "Navy banded (customised)". */
export const describeTableStyle = (choice: TableStyleChoice): string =>
  `${(LOOK_PRESETS[choice.preset] ?? LOOK_PRESETS.classic).label}${
    isCustomised(choice) ? " (customised)" : ""
  }`
